import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import '@/styles/common/publish-scroll-jump-fab.css';

/**
 * 发品 / 产品模板长页：类目滚出可视区后，在右下角提供回到顶部 / 回到底部。
 * @param {'tpo'|'spo'|'tko'} prefix
 */
export default function PublishScrollJumpFab({ prefix = 'tpo' }) {
  const hostRef = useRef(null);
  const scrollRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const host = hostRef.current;
    const panel = host?.closest(`.${prefix}-wizard__panel`);
    const scroll = panel?.querySelector(`.${prefix}-wizard__panel-scroll`);
    if (!scroll) return undefined;
    scrollRef.current = scroll;

    const update = () => {
      const category = scroll.querySelector('[data-field-error="categoryId"]');
      if (!category) {
        setVisible(scroll.scrollTop > 48);
        return;
      }
      const rootRect = scroll.getBoundingClientRect();
      const catRect = category.getBoundingClientRect();
      const obscured = catRect.bottom < rootRect.top + 8 || catRect.top > rootRect.bottom - 8;
      setVisible(obscured);
    };

    update();
    scroll.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      scroll.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [prefix]);

  const scrollToTop = useCallback(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, []);

  return (
    <div
      ref={hostRef}
      className={`publish-scroll-jump${visible ? ' is-visible' : ''}`}
      aria-hidden={!visible}
    >
      <button
        type="button"
        className="publish-scroll-jump__btn"
        onClick={scrollToTop}
        tabIndex={visible ? 0 : -1}
        aria-label="回到顶部"
        title="回到顶部"
      >
        <ChevronUp size={20} strokeWidth={2.2} aria-hidden="true" />
      </button>
      <button
        type="button"
        className="publish-scroll-jump__btn"
        onClick={scrollToBottom}
        tabIndex={visible ? 0 : -1}
        aria-label="回到底部"
        title="回到底部"
      >
        <ChevronDown size={20} strokeWidth={2.2} aria-hidden="true" />
      </button>
    </div>
  );
}
